import {
  Card,
  Divider,
  ModalDialog,
  PrimaryButton,
  Space,
  TertiaryButton,
  TextLoader,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { MatchedItemPicker } from '@webapp/modules/accounts-payable/features/attach-invoice-to-matched-items/presenters/AttachInvoiceToMatchedItemsPresenter'
import { CreateCreditCardBillFormPresenter } from '@webapp/modules/accounts-payable/features/create-credit-card-bill/presenters/CreateCreditCardBillFormPresenter'
import { BillMatchedItemListPresenter } from '@webapp/modules/accounts-payable/features/list-bill-matched-items/presenters/BillMatchedItemListPresenter'
import { CreateVendorPaymentMethodFormPresenter } from '@webapp/modules/vendor'
import messages from './messages'
import { useCreateCreditCardBillView } from './useCreateCreditCardBillView'

export interface ICreateCreditCardBillViewProps {
  accountId: number
}

/**
 * Feature Set: Create Credit Card Bill View (Page)
 *
 * Composes the bill form (with the matched-items table in its slot), bridges
 * the cross-module payment-method create result (vendor → accounts-payable,
 * ADR-6), forwards feature 21's matches to the attach-invoice picker, and owns
 * both FS modals plus the post-submit redirect and cancel navigation.
 */
export const CreateCreditCardBillView = ({
  accountId,
}: ICreateCreditCardBillViewProps) => {
  const intl = useProcurifyIntl()
  const { state, methods } = useCreateCreditCardBillView({ accountId })
  const { account, session } = state

  if (state.isLoading && !account) {
    return <TextLoader />
  }

  if (!account || !session) {
    return null
  }

  return (
    <>
      <Card>
        <Card.Content>
          <CreateCreditCardBillFormPresenter
            account={account}
            session={session}
            additionalPaymentMethods={state.additionalPaymentMethods}
            matchedItemsSlot={
              <BillMatchedItemListPresenter sessionId={session.id} />
            }
            onSubmitted={methods.redirectToPendingBills}
            onAddPaymentMethod={methods.openPaymentMethodModal}
            onAttachInvoice={methods.openAttachInvoiceModal}
            onCancel={methods.goBackToMatch}
          />
        </Card.Content>
      </Card>

      <ModalDialog
        title={intl.formatMessage(messages.addPaymentMethodModalTitle, {
          provider: account.vendor.name,
        })}
        maxWidth='md'
        open={state.isPaymentMethodModalOpen}
        onClose={methods.closePaymentMethodModal}
      >
        {state.isPaymentMethodModalOpen && (
          <CreateVendorPaymentMethodFormPresenter
            vendor={account.vendor}
            defaultCurrencyId={account.currency.id}
            onCreated={methods.handlePaymentMethodCreated}
            onCancel={methods.closePaymentMethodModal}
          />
        )}
      </ModalDialog>

      {/* FS owns the attach-invoice modal + confirm button (BHV-048) */}
      <ModalDialog
        title={intl.formatMessage(messages.attachInvoiceModalTitle)}
        maxWidth='sm'
        open={state.attachInvoiceFileUuid !== null}
        onClose={methods.closeAttachInvoiceModal}
      >
        {state.attachInvoiceFileUuid !== null && (
          <Space>
            <MatchedItemPicker
              matches={state.matches}
              selectedItemIds={state.attachSelectedItemIds}
              onToggleItem={methods.toggleAttachItem}
            />
            <Divider />
            <Space direction='horizontal' justify='end' align='center'>
              <TertiaryButton
                type='button'
                onClick={methods.closeAttachInvoiceModal}
                disabled={state.isAttaching}
              >
                {intl.formatMessage(messages.attachCancelButton)}
              </TertiaryButton>
              <PrimaryButton
                type='button'
                isLoading={state.isAttaching}
                disabled={state.attachSelectedItemIds.length === 0}
                onClick={methods.confirmAttachInvoice}
              >
                {intl.formatMessage(messages.attachConfirmButton)}
              </PrimaryButton>
            </Space>
          </Space>
        )}
      </ModalDialog>
    </>
  )
}

CreateCreditCardBillView.displayName = 'CreateCreditCardBillView'
